import React, {useEffect, useRef, useState} from "react";
import * as Yup from "yup";
import md5 from "md5";
import {useIntl} from "react-intl";
import {useFormik} from "formik";
import Countdown from "react-countdown";
import {addMinutes, addSeconds} from "date-fns";
import {Auth, Forms, notify} from "@housebets/housebets-x-package";
import {CodeInput} from '@housebets/housebets-x-package/lib/components/form/CodeInput';
import {EmailHint} from "@housebets/housebets-x-package/lib/components/feature/forms/2FA";
import {WarningIconV2} from "@housebets/housebets-x-package/lib/components/icons";

import apiUser from "app/api/user";
import {useAppSelector} from "hooks/useAppSelector";
import apiAuth from "app/api/auth";
import {loginSuccess} from "app/store/slices/user";
import {setLoginType} from "app/store/slices/setting";
import {LoginType} from "app/types/common";
import {IVerificationProps} from "./types";
import {useGTMEvents} from "app/hooks/useGTMEvents";
import _ from 'lodash';
import { useDispatch } from "react-redux";
const { replace } = _;

const SignInFormSchema = () =>
    Yup.object().shape({
        pin: Yup.string().nullable(),
    });

export const Verification: React.FC<IVerificationProps> = ({
    userData,
    isShowPinPanel,
    setActiveTab,
    setShowPinPanel,
    isShowVerificationButton,
    setShowVerificationButton,
}) => {
    const {$t} = useIntl();
    const {registration, form} = useGTMEvents();
    const dispatch = useDispatch();

    const [timeEnd, setTimeEnd] = useState(true);
    const ref = useRef<Countdown | null>(null);
    const [date, setDate] = useState<Date>(addMinutes(new Date(), 0));
    const [sended, setSended] = useState(false);

    const promoCode = useAppSelector((state) => state.setting.promoCode);

    const getCode = async () => {
        const {status, data} = await apiUser.getVerificationCode("email", userData.email, "EMAIL_VERIFICATION", userData.name);
        if (status === 200 && data.code === 0) {
            notify({
                type: "success",
                title: $t({id: "MailSentSuccess"}),
            });
            setActiveTab((prev) => prev + 1);
            setShowPinPanel(true);
            setShowVerificationButton(false);
            return;
        }
        notify({
            type: "error",
            title: $t({id: `VerifyPhoneFail${data.code}`}),
        });
    };

    const formik = useFormik({
        initialValues: {
            pin: "",
        },
        validationSchema: SignInFormSchema(),
        validateOnBlur: true,
        onSubmit: async (values) => {
            try {
                setDate(addSeconds(new Date(), 10));
                setTimeEnd(false);
                setSended(true);
                const {pin} = values;
                const {name, email, password, referral = ""} = userData;

                let inviteCodeParam = referral;
                let promoCodeParam = promoCode;
                if (inviteCodeParam.length > 0 && /[a-zA-Z]/.test(inviteCodeParam)) {
                    promoCodeParam = inviteCodeParam;
                    inviteCodeParam = "";
                }

                const res = await apiAuth.registerEmail({
                    nickName: name,
                    email: replace(email, /\s+/g, ""),
                    verifiactionCode: pin + "",
                    password: md5(password),
                    birth: "",
                    promoCode: promoCodeParam,
                    inviteCode: inviteCodeParam,
                    affilkaTag: "",
                });

                const {status, data} = res;
                console.log("res", data.data.userId);
                if (status === 200 && data.code === 0) {
                    registration.complete({
                        affiliate_code: inviteCodeParam,
                        bonus_code: "",
                        currency: data.data.userMoneyType,
                        marketing_opt_in: false,
                        promo_code: promoCodeParam,
                        registration_method: "email",
                        signup_source: window.location.pathname,
                        user_id: data.data.userId,
                        user_type: "regular",
                    });
                    dispatch(loginSuccess(data.data));
                    dispatch(setLoginType(LoginType.HIDE));
                    return;
                }
                formik.setErrors({
                    pin: "Invalid pin",
                });
                form.error({
                    message: "Invalid pin",
                    field: "pin",
                    form_id: "signup_pin_verification_form",
                    form_name: "Sign Up Pin Verification Form",
                });
                if (data.code === 101) {
                    notify({
                        type: "error",
                        title: $t({id: "SystemError"}),
                    });
                    form.error({
                        message: $t({id: "SystemError"}),
                        field: "pin",
                        form_id: "signup_pin_verification_form",
                        form_name: "Sign Up Pin Verification Form",
                    });
                    return;
                }
                notify({
                    type: "error",
                    title: data.code ? $t({id: `REGISTER_ERROR_${data.code}`}) : data.message,
                });
                form.error({
                    message: data.code ? $t({id: `REGISTER_ERROR_${data.code}`}) : data.message,
                    field: "pin",
                    form_id: "signup_pin_verification_form",
                    form_name: "Sign Up Pin Verification Form",
                });
            } catch (error: any) {
                formik.setErrors({
                    pin: error.message,
                });
                form.error({
                    message: error.message,
                    field: "pin",
                    form_id: "signup_pin_verification_form",
                    form_name: "Sign Up Pin Verification Form",
                });
            }
        },
    });

    useEffect(() => {
        if (isShowPinPanel) {
            setTimeEnd(true);
            setDate(addMinutes(new Date(), 0));
            setSended(false);
        }
    }, [isShowPinPanel]);

    return (
        <Auth.Verification
            getCode={getCode}
            showVerificationButton={isShowVerificationButton}
            showPinPanel={isShowPinPanel}
            pinSection={
                <Forms.TFA.Wrapper>
                    <form id="signup_pin_verification_form" autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                        <Forms.TFA.Row>
                            <Forms.TFA.Text margin title={$t({id: "EmailVerification"})} subtitle={$t({id: "PleaseEnterCode"})} />
                        </Forms.TFA.Row>
                        <Forms.TFA.Row padding>
                            <CodeInput
                                label=""
                                name="pin"
                                inputMode="numeric"
                                type="text"
                                disabled={false}
                                fields={6}
                                value={formik.values.pin}
                                error={
                                    formik?.errors?.pin
                                        ? {
                                              message: "",
                                          }
                                        : null
                                }
                                touch={() =>
                                    formik.setTouched({
                                        pin: true,
                                    })
                                }
                                untouch={() =>
                                    formik.setTouched({
                                        pin: false,
                                    })
                                }
                                onChange={(v) => {
                                    formik.setValues({
                                        pin: v,
                                    });

                                    formik.setTouched({
                                        pin: true,
                                    });
                                }}
                            />
                        </Forms.TFA.Row>
                        <Forms.TFA.Row padding>
                            <EmailHint
                                icon={<WarningIconV2 />}
                                text={
                                    // eslint-disable-next-line quotes
                                    $t({id: "EmailHintDontSeeMail"})
                                }
                            />
                        </Forms.TFA.Row>
                        <Forms.TFA.Row padding>
                            <Forms.TFA.TFACounter>
                                {!timeEnd ? (
                                    <Countdown
                                        ref={ref}
                                        date={date}
                                        onComplete={() => {
                                            setTimeEnd(true);
                                            setSended(false);
                                        }}
                                        renderer={({minutes, seconds}) => {
                                            return (
                                                <div>
                                                    {minutes < 10 ? `0${minutes}` : minutes}: {seconds < 10 ? `0${seconds}` : seconds}
                                                </div>
                                            );
                                        }}
                                    />
                                ) : (
                                    <div>00:00</div>
                                )}
                            </Forms.TFA.TFACounter>
                        </Forms.TFA.Row>
                        <Forms.TFA.Button isAbsolute type="submit" disabled={sended} text={$t({id: "Verify"})} />
                    </form>
                </Forms.TFA.Wrapper>
            }
        />
    );
};
